import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { Sheet, SheetContent } from '@goaltree/ui/components/sheet'
import { useAction } from 'convex/react'
import { TargetIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { useGoals } from '@/hooks/use-goals'
import { CATEGORY_COLORS, useCategoryFill } from '@/lib/category-color'

interface GoalPickerSheetProps {
  taskId: Id<'nodes'> | null
  onDone: () => void
  onCancel: () => void
}

export function GoalPickerSheet({ taskId, onDone, onCancel }: GoalPickerSheetProps) {
  const { t } = useTranslation()
  const fill = useCategoryFill()
  const goals = useGoals()
  const moveToGoal = useAction(api.goalAddTaskNode.moveToGoal)
  const [linking, setLinking] = useState<string | null>(null)

  const linkableGoals = (goals ?? []).filter(
    (g) => !g.isInbox && g.status !== 'done' && g.milestoneCount > 0,
  )

  async function handleSelect(goalId: Id<'nodes'>) {
    if (!taskId) return
    setLinking(goalId as string)
    try {
      const r = await moveToGoal({ taskId, goalId })
      toast.success(t('inbox.movedTo', { milestone: r.milestoneTitle, target: r.targetTitle }))
      onDone()
    } catch (e) {
      const msg = (e as { data?: string })?.data ?? t('inbox.linkFailed')
      toast.error(msg)
    } finally {
      setLinking(null)
    }
  }

  return (
    <Sheet open={!!taskId} onOpenChange={(open) => !open && onCancel()}>
      <SheetContent side="right" className="flex w-80 flex-col p-0 sm:max-w-sm">
        <div className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto px-4 pt-8 pb-4 flex flex-col gap-5">
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-widest text-muted-foreground/60">
                {t('inbox.linkToGoal')}
              </p>
              <p className="text-2xl font-bold leading-tight text-foreground">
                {t('inbox.pickAGoal')}
              </p>
            </div>

            {linkableGoals.length === 0 ? (
              <div className="flex flex-col items-center gap-3 py-8 text-center">
                <TargetIcon className="h-10 w-10 text-muted-foreground/20" strokeWidth={1.5} />
                <p className="text-sm text-muted-foreground">{t('inbox.noGoalsToLink')}</p>
              </div>
            ) : (
              <div className="flex flex-col gap-1.5">
                {linkableGoals.map((goal) => {
                  const color = fill(CATEGORY_COLORS[goal.category.toLowerCase()] ?? '#6b7280')
                  const isLinking = linking === (goal._id as string)
                  return (
                    <button
                      key={goal._id}
                      type="button"
                      disabled={!!linking}
                      onClick={() => handleSelect(goal._id as Id<'nodes'>)}
                      className="group flex cursor-pointer items-center gap-2 rounded-xl border border-border/60 bg-muted/30 px-3 py-2.5 text-left transition-colors hover:border-border hover:bg-muted/60 disabled:opacity-50"
                    >
                      <span
                        className="h-2 w-2 shrink-0 rounded-full"
                        style={{ backgroundColor: color }}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
                          {t(`goals.cat.${goal.category.toLowerCase()}`, {
                            defaultValue: goal.category,
                          })}
                        </p>
                        <p className="line-clamp-2 text-sm font-medium leading-snug text-foreground">
                          {goal.title}
                        </p>
                      </div>
                      {isLinking ? (
                        <span className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent text-muted-foreground/40" />
                      ) : null}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
